// Phase T2.0A Prompt 2 — Mobile-safe Withdraw confirmation.
//
// Replaces window.confirm() so the confirmation renders inside the shared
// mobile-safe Dialog primitive (sticky footer, safe-area padding, no
// off-screen CTAs). Destructive styling on the confirm button.

import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter,
  DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Warning } from '@phosphor-icons/react';

export default function WithdrawConfirmDialog({
  open, onOpenChange, onConfirm, busy = false,
  title = 'Withdraw from this event?',
  description = 'This will remove your registration.',
  confirmLabel = 'Withdraw',
  // When true, the dialog surfaces a paid-safety warning + acknowledgement
  // checkbox before enabling the destructive button. Backend continues to
  // just delete the registration — payments live in a separate table and
  // remain intact for admin refund handling.
  viewerPaid = false,
}) {
  const [ack, setAck] = useState(false);
  const disableConfirm = busy || (viewerPaid && !ack);
  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setAck(false); }}>
      <DialogContent
        data-testid="withdraw-confirm-dialog"
        className="sm:max-w-md flex flex-col"
      >
        <DialogHeader>
          <DialogTitle className="pr-6">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {viewerPaid ? (
            <div
              data-testid="withdraw-paid-warning"
              className="space-y-3 rounded-xl border border-[#c9a93a]/50 bg-[#fff9e6]/70 p-3"
            >
              <div className="flex items-start gap-2">
                <Warning size={18} weight="fill" className="text-[#7c5b1a] mt-0.5 shrink-0" />
                <div className="text-[13px] text-[#3a2c05]">
                  <div className="font-semibold">You&rsquo;ve already paid for this event.</div>
                  <div className="mt-1 text-[#5a4b1e]">
                    Withdrawing does NOT automatically refund your payment.
                    Contact an admin to arrange a refund.
                  </div>
                </div>
              </div>
              <label className="flex items-start gap-2 text-[12.5px] text-[#3a2c05] cursor-pointer">
                <input
                  type="checkbox"
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                  data-testid="withdraw-paid-ack"
                  className="mt-0.5"
                />
                <span>I understand withdrawing will not refund my payment.</span>
              </label>
            </div>
          ) : (
            <div className="text-[13.5px] text-[#2a332c]">
              You can re-register any time while registration is open.
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="withdraw-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={disableConfirm}
            className="bg-[#8a1c1c] text-white hover:bg-[#6b1414]"
            data-testid="withdraw-confirm"
          >
            {busy ? 'Withdrawing…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
